// Replays queued offline visit-report PATCHes when network is restored.
// AC §17: offline mobile report syncs to server within 60 seconds of reconnect.
//
// Usage: call once at app root — useOfflineSync() in App.tsx or main.tsx.
//
// Q9 S7 — single-store replay against `pending-section-patches`
// (D13a insertion-ordered). The legacy `pending-reports` store
// was dropped in S7's IDB v3 migration; nothing replays through
// the retired POST /v1/visit-reports endpoint anymore.

import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  getPendingSectionPatches, deleteSectionPatch, bumpRetryCount,
  pendingSectionPatchCount,
} from "../lib/offline-queue-sections.js";
import { patchSection, submitVisitReport } from "../api/visit-report-api.js";
import type { SectionStep } from "../lib/visit-report-conditional-rules.js";

async function replaySectionPatch(entry: { id?: number; sr_id: string; section: string; payload: Record<string, unknown> }): Promise<boolean> {
  if (entry.section === "submit") {
    // Submit entries carry the full submit body (incl. visible_steps + completed_at).
    // Cast through unknown — the payload was serialized by the wizard's submit
    // path so the runtime shape matches SubmitBody, but TS can't see the bridge.
    const body = entry.payload as unknown as Parameters<typeof submitVisitReport>[1];
    await submitVisitReport(entry.sr_id, body);
    return true;
  }
  await patchSection(entry.sr_id, entry.section as SectionStep, entry.payload);
  return true;
}

async function replaySectionQueue(): Promise<void> {
  const count = await pendingSectionPatchCount();
  if (count === 0) return;
  const entries = await getPendingSectionPatches();
  // Insertion-ordered (D13a): the auto-increment id keeps order stable
  // even if the IDB getAll() implementation doesn't guarantee it.
  entries.sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
  for (const entry of entries) {
    try {
      await replaySectionPatch(entry);
      if (entry.id !== undefined) await deleteSectionPatch(entry.id);
    } catch {
      // Leave the entry in the queue; bump retry_count so a future
      // operator can spot a stuck entry.
      if (entry.id !== undefined) await bumpRetryCount(entry.id);
    }
  }
}

export function useOfflineSync(): void {
  const qc          = useQueryClient();
  const replayingRef = useRef(false);

  async function replay(): Promise<void> {
    if (replayingRef.current) return;
    replayingRef.current = true;
    try {
      await replaySectionQueue();
    } finally {
      replayingRef.current = false;
    }
    void qc.invalidateQueries({ queryKey: ["sr"] });
    void qc.invalidateQueries({ queryKey: ["visit-reports"] });
  }

  useEffect(() => {
    // Replay immediately in case we're online and have queued items (e.g. app reload)
    if (navigator.onLine) void replay();

    const handler = (): void => { void replay(); };
    window.addEventListener("online", handler);
    return (): void => window.removeEventListener("online", handler);
  }, []); // intentional: replay is closure-stable via useRef; no deps needed
}
